import { Link, createFileRoute } from "@tanstack/react-router"
import { ArrowLeft } from "lucide-react"
import { IDD_ART_LABELS, type IddNachweis } from "@nl/shared"

import { formatDatumZeit, formatMinuten, formatProzent } from "@/components/idd/idd-format"
import { LoadState } from "@/components/load-state"
import { PrintButton } from "@/components/print-button"
import { Button } from "@/components/ui/button"
import { useIddNachweis } from "@/lib/idd-queries"

const THEMENFELD: Record<IddNachweis["art"], string> = {
  gespraech: "Kundenberatung und Gesprächsführung",
  produkt: "Produktkenntnisse Altersvorsorge",
}

function Zeile({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid gap-1 py-2.5 sm:grid-cols-[180px_1fr] sm:gap-4">
      <dt className="text-[13px] text-muted-foreground">{label}</dt>
      <dd className="text-[14.5px]">{children}</dd>
    </div>
  )
}

/**
 * The certificate. Saved as PDF through the browser's print dialog, like
 * the debrief: nothing leaves the machine, and the page is the document.
 */
function Zertifikat({ nachweis }: { nachweis: IddNachweis }) {
  return (
    <article
      className="mx-auto max-w-3xl space-y-7 rounded-2xl p-6 ring-1 ring-foreground/10 sm:p-10 print:ring-0"
      aria-label="IDD-Weiterbildungsnachweis"
    >
      <header className="space-y-3 border-b border-[var(--rule)] pb-6">
        <p className="text-[12px] font-bold tracking-[0.08em] text-primary uppercase">
          neue leben · KI-Coach
        </p>
        <h1 className="font-display text-[30px] leading-tight font-medium tracking-tight">
          Weiterbildungsnachweis nach IDD
        </h1>
        <p className="text-[14px] text-muted-foreground">
          Bescheinigung über eine anrechenbare Lernaktivität im Rahmen der
          Weiterbildungspflicht von 15 Stunden je Kalenderjahr (§ 34d Abs. 9 GewO, § 48 Abs. 2 VAG).
        </p>
      </header>

      <dl className="divide-y divide-[var(--rule)]">
        <Zeile label="Teilnehmer:in">
          <span className="font-semibold">{nachweis.teilnehmer}</span>
          {nachweis.sparkasse && <span className="text-muted-foreground"> · {nachweis.sparkasse}</span>}
        </Zeile>
        <Zeile label="Lernmodul">
          {nachweis.titel}
          <span className="block text-[13px] text-muted-foreground">{IDD_ART_LABELS[nachweis.art]}</span>
        </Zeile>
        <Zeile label="Themenfeld">{THEMENFELD[nachweis.art]}</Zeile>
        <Zeile label={nachweis.art === "gespraech" ? "Erfüllte Pflichtschritte" : "Bearbeitete Lernbausteine"}>
          <ul className="list-disc space-y-0.5 pl-5">
            {nachweis.inhalte.map((i) => (
              <li key={i}>{i}</li>
            ))}
          </ul>
        </Zeile>
        {nachweis.quote !== undefined && (
          <Zeile label="Lernerfolgskontrolle">Bestanden mit {formatProzent(nachweis.quote)}</Zeile>
        )}
        <Zeile label="Angerechnete Lernzeit">
          <span className="font-semibold">{formatMinuten(nachweis.minuten)}</span>
        </Zeile>
        <Zeile label="Abgeschlossen am">{formatDatumZeit(nachweis.abgeschlossenAm)} Uhr</Zeile>
        <Zeile label="Kalenderjahr">{nachweis.jahr}</Zeile>
        <Zeile label="Lernform">Digitales Training mit KI-Coach, selbstgesteuert</Zeile>
      </dl>

      <footer className="flex flex-wrap justify-between gap-2 border-t border-[var(--rule)] pt-4 text-[12px] text-muted-foreground">
        <span>Nachweis-Nr. {nachweis.nummer}</span>
        <span>Regelwerk {nachweis.katalogFassung}</span>
      </footer>
    </article>
  )
}

function NachweisPage() {
  const { id } = Route.useParams()
  const { data, isError, refetch } = useIddNachweis(id)
  return (
    <div className="space-y-5">
      <div className="no-print flex flex-wrap items-center justify-between gap-2">
        <Button asChild variant="ghost" size="sm" className="-ml-2">
          <Link to="/idd">
            <ArrowLeft className="h-4 w-4" />
            IDD-Bereich
          </Link>
        </Button>
        {data && <PrintButton />}
      </div>
      {data ? (
        <Zertifikat nachweis={data} />
      ) : (
        <LoadState failed={isError} onRetry={() => void refetch()}>
          Der Nachweis konnte nicht geladen werden.
        </LoadState>
      )}
    </div>
  )
}

export const Route = createFileRoute("/_app/idd/nachweis/$id")({
  component: NachweisPage,
})
